import { useEffect, useState, useContext, useRef, useCallback } from 'react'
import axios from 'axios'

import AuthenticationContext from '../../../../src/contexts/Authentication/AuthenticationContext'
import ToasterContext from '../../../../src/contexts/Toaster/ToasterContext'
import { loadCollections, saveCollections, clearCollections } from '../Database.services/db'

// 🚀 PRODUCTION VERSION - Using backend endpoints only

const useDbCollections = (collectionNames = []) => {
  const [collections, setCollections] = useState({})
  const [loading, setLoading] = useState(true)
  const [errors, setErrors] = useState({})
  const previousUserRef = useRef()
  const isInitializingRef = useRef(false) // Prevent overlapping async operations
  const collectionsStringRef = useRef() // Stabilize collectionNames dependency

  const {
    isAuthenticated = false,
    user,
    isLoading: authLoading,
  } = useContext(AuthenticationContext)

  const {
    showToast,
  } = useContext(ToasterContext)

  // Stabilize collectionNames dependency
  const collectionsString = JSON.stringify(collectionNames)
  if (collectionsStringRef.current !== collectionsString) {
    collectionsStringRef.current = collectionsString
  }

  // Helper to get user cache key for comparison
  const getUserCacheKey = useCallback((userObj) => {
    if (!userObj) return 'anonymous'
    return userObj.email || userObj.id || 'unknown'
  }, [])

  const currentUserKey = getUserCacheKey(user)
  const previousUserKey = getUserCacheKey(previousUserRef.current)

  // Stable fetch function for dashboard sales orders
  const fetchDashboardSalesOrders = useCallback(async (authenticatedUser) => {
    console.log('🔄 Loading dashboardSalesOrders from backend endpoint...')
    try {
      const response = await axios.post('/dashboarding/getDashboardSalesOrdersCollection', {
        // The backend handles user filtering and authentication automatically
      })
      console.log(`📊 Server-filtered data received: ${response.data.collection?.length || 0} records`)
      return response.data
    } catch (error) {
      console.error('❌ Backend endpoint failed:', error)

      // Show user-friendly error message in toast
      const errorMessage = error.response?.data?.message
        || error.message
        || 'Erreur de connexion au serveur backend'

      showToast({
        message: `❌ Erreur backend: ${errorMessage}`,
        type: 'error',
        duration: 8000,
      })

      throw error
    }
  }, [showToast])

  // Generic function to get any collection from backend
  const getCollection = useCallback(async (collectionName) => {
    console.log(`🔄 [getCollection] Loading ${collectionName}...`)
    try {
      const response = await axios.post('/getCollection', { collectionName })
      console.log(`✅ [getCollection] ${collectionName} loaded: ${response.data.collection?.length || 0} records`)
      return response.data.collection || []
    } catch (error) {
      console.error(`❌ [getCollection] Failed to load ${collectionName}:`, error)
      throw error
    }
  }, [])

  // Stable fetch all function
  const fetchAll = useCallback(async (targetUser) => {
    if (authLoading || isInitializingRef.current) {
      console.log('⏳ Already initializing or auth loading, skipping fetch')
      return null
    }

    isInitializingRef.current = true
    console.log(`🔄 Fetching fresh data for user: ${getUserCacheKey(targetUser)}`)

    try {
      const newCollections = {}
      const newErrors = {}

      await Promise.all(
        collectionNames.map(async (collectionName) => {
          try {
            let response

            if (collectionName === 'dashboardSalesOrders') {
              response = await fetchDashboardSalesOrders(user) // Pass user for mock filtering
            } else {
              const collection = await getCollection(collectionName)
              response = { collection }
            }

            const { collection = [] } = response
            newCollections[collectionName] = collection
          } catch (e) {
            console.error(`❌ Error loading collection ${collectionName}:`, e)
            newErrors[collectionName] = e
          }
        }),
      )

      // Save to IndexedDB with user context
      await saveCollections(newCollections, targetUser)

      return { collections: newCollections, errors: newErrors }
    } finally {
      isInitializingRef.current = false
    }
  }, [collectionNames, authLoading])

  // Main initialization effect
  useEffect(() => {
    let isMounted = true

    const init = async () => {
      // Prevent multiple simultaneous initializations
      if (isInitializingRef.current) {
        console.log('⏳ Already initializing, skipping')
        return
      }

      // Skip if auth is still loading
      if (authLoading) {
        console.log('⏳ Auth still loading, maintaining current state')
        return
      }

      // Check if user changed
      const userChanged = previousUserKey && currentUserKey !== previousUserKey
      if (userChanged) {
        console.log(`🔄 User changed from ${previousUserKey} to ${currentUserKey}`)

        // Set loading immediately for user change
        if (isMounted) {
          setLoading(true)
        }

        // Try to load cache for new user
        const cachedForNewUser = await loadCollections(user)
        if (cachedForNewUser && Object.keys(cachedForNewUser).length > 0) {
          console.log(`📦 Found cache for new user ${currentUserKey} - using it`)
          if (isMounted) {
            setCollections(cachedForNewUser)
            setLoading(false)
            setErrors({})
          }
        } else {
          console.log(`📦 No cache found for new user ${currentUserKey} - fetching fresh data`)
          const result = await fetchAll(user)
          if (result && isMounted) {
            // 🔍 DEBUG - Diagnostic pour problème prod
            console.log('🔍 DEBUG - fetchAll completed (user changed):', {
              resultKeys: Object.keys(result.collections || {}),
              bankTransfersCount: result.collections?.bankTransfers?.length || 0,
              deckOrdersCount: result.collections?.deckOrders?.length || 0,
              transfersOrdersMatchesCount: result.collections?.transfersOrdersMatches?.length || 0,
              isMounted,
              timestamp: new Date().toISOString(),
            })
            setCollections(result.collections)
            setErrors(result.errors)
            setLoading(false)
          }
        }

        // Update user reference
        previousUserRef.current = user
        return
      }

      // First load or same user - try cache first
      const cached = await loadCollections(user)
      
      // 🔍 DEBUG - Diagnostic pour problème prod
      console.log('🔍 DEBUG - Cache check result:', {
        hasCached: !!cached,
        cachedKeys: cached ? Object.keys(cached) : [],
        cachedBankTransfersCount: cached?.bankTransfers?.length || 0,
        cachedDeckOrdersCount: cached?.deckOrders?.length || 0,
        cachedTransfersOrdersMatchesCount: cached?.transfersOrdersMatches?.length || 0,
        isAuthenticated,
        authLoading,
        currentUserKey,
      })
      
      if (cached && Object.keys(cached).length > 0) {
        console.log(`📦 Using cached collections for user: ${currentUserKey}`)
        // 🔍 DEBUG - Diagnostic pour problème prod
        console.log('🔍 DEBUG - Using cache with data:', {
          bankTransfersCount: cached?.bankTransfers?.length || 0,
          deckOrdersCount: cached?.deckOrders?.length || 0,
          transfersOrdersMatchesCount: cached?.transfersOrdersMatches?.length || 0,
          timestamp: new Date().toISOString(),
        })
        if (isMounted) {
          setCollections(cached)
          setLoading(false)
          setErrors({})
        }
      } else if (isAuthenticated && !authLoading) {
        console.log(`🔄 No cache found for user ${currentUserKey}, fetching fresh data`)
        const result = await fetchAll(user)
        if (result && isMounted) {
          // 🔍 DEBUG - Diagnostic pour problème prod
          console.log('🔍 DEBUG - fetchAll completed (first load):', {
            resultKeys: Object.keys(result.collections || {}),
            bankTransfersCount: result.collections?.bankTransfers?.length || 0,
            deckOrdersCount: result.collections?.deckOrders?.length || 0,
            transfersOrdersMatchesCount: result.collections?.transfersOrdersMatches?.length || 0,
            isMounted,
            timestamp: new Date().toISOString(),
          })
          setCollections(result.collections)
          setErrors(result.errors)
          setLoading(false)
        }
      } else if (!authLoading) {
        if (isMounted) {
          setLoading(false)
        }
      }

      // Update user reference
      previousUserRef.current = user
    }

    init()

    return () => {
      isMounted = false
    }
  }, [
    collectionsStringRef.current,
    isAuthenticated,
    authLoading,
    currentUserKey,
    previousUserKey,
  ])

  // Stable refetch function
  const refetch = useCallback(async (targetCollections = null) => {
    const collectionsToRefetch = targetCollections || collectionNames

    if (authLoading || isInitializingRef.current) {
      console.log('⏳ Auth loading or initializing, skipping refetch')
      return
    }

    setLoading(true)
    await clearCollections(user) // Clear cache for current user only

    const newCollections = {}
    const newErrors = {}

    await Promise.all(
      collectionsToRefetch.map(async (collectionName) => {
        try {
          let response
          if (collectionName === 'dashboardSalesOrders') {
            response = await fetchDashboardSalesOrders(user) // Pass user for mock filtering
          } else {
            const collection = await getCollection(collectionName)
            response = { collection }
          }

          const { collection = [] } = response
          newCollections[collectionName] = collection
          delete newErrors[collectionName]
        } catch (e) {
          console.error(`❌ Error refetching collection ${collectionName}:`, e)
          newErrors[collectionName] = e
        }
      }),
    )

    await saveCollections(newCollections, user) // Save with user context
    setCollections(newCollections)
    setErrors(newErrors)
    setLoading(false)
  }, [collectionNames, authLoading, JSON.stringify(user)])

  // Fonction pour récupérer un document spécifique
  const getDocument = useCallback(async (collectionName, docId) => {
    console.log(`🔍 [getDocument] Getting ${collectionName} document ID: ${docId}`)
    
    try {
      const response = await axios.post('/getDocument', {
        collectionName,
        docId,
      })
      
      console.log(`✅ [getDocument] Document retrieved successfully`)
      return { success: true, data: response.data }
    } catch (error) {
      console.error(`❌ [getDocument] Failed to get document:`, error)
      
      const errorMessage = error.response?.data?.message 
        || error.message 
        || 'Erreur lors de la récupération du document'
        
      return { success: false, error: errorMessage }
    }
  }, [])

  // Fonction pour modifier un document utilisateur (simulation permissions SuperAdmin)
  const setDocument = useCallback(async (collectionName, docId, updatedData) => {
    console.log(`🔧 [setDocument] Updating ${collectionName} document ID: ${docId}`)
    
    try {
      const response = await axios.post('/setDocument', {
        collectionName,
        docId,
        data: updatedData,
      })
      
      console.log(`✅ [setDocument] Document updated successfully`)
      
      // Optionnellement, recharger la collection users pour voir les changements
      if (collectionName === 'users') {
        await refetch(['users'])
      }

      return { success: true, data: response.data }
    } catch (error) {
      console.error(`❌ [setDocument] Failed to update document:`, error)

      const errorMessage = error.response?.data?.message 
        || error.message 
        || 'Erreur lors de la modification du document'

      showToast({
        message: `❌ Modification échouée: ${errorMessage}`,
        type: 'error',
        duration: 8000,
      })
      
      return { success: false, error: errorMessage }
    }
  }, [refetch, showToast])

  return {
    collections,
    updateCollections: setCollections,
    loading,
    errors,
    refetch,
    setDocument,
    getDocument,
    getCollection,
  }
}

export default useDbCollections
